import {
  SERVICE_STATES,
  SERVICE_TYPES
} from "@/modules/Services/constants/ServicesConstants";
import { TASK_STATES } from "@/modules/Tasks/constants/TaskConstants";
import { USER_STATES } from "@/modules/User/constants/UserConstants";
import {
  DATE_OPERATORS,
  OPERATORS_FILTERS
} from "@/shared/constants/GeneralConstants";
import { ChartOptions } from "chart.js";
import { Filter } from "../models/Filter";
import { TableHeader } from "../models/TableHeader";
import {
  ORDER_CONSTANTS,
  ORDER_STATES
} from "@/modules/Orders/constants/OrderConstants";

export const COLLECTIONS = {
  ORDER: "medicalOrders"
};
export const COLLECTIONS_LABELS_TRANSLATE = {
  SECTION_EN: "sections",
  GROUP_EN: "groups",
  SECTION_ES: "Secciones",
  GROUP_ES: "Grupos"
};
export function SERVICE_TYPE_TRANSLATE(
  serviceTypeName: string,
  chartData?: any
) {
  const translations = {
    [COLLECTIONS_LABELS_TRANSLATE.GROUP_EN]:
      COLLECTIONS_LABELS_TRANSLATE.GROUP_ES,
    [COLLECTIONS_LABELS_TRANSLATE.SECTION_EN]:
      COLLECTIONS_LABELS_TRANSLATE.SECTION_ES
  };
  if (chartData && chartData.extraInfo) {
    const visualName = chartData.extraInfo.find(
      (label: any) => label.name == serviceTypeName
    )?.visualName;
    return visualName ?? translations[serviceTypeName] ?? serviceTypeName;
  }

  return translations[serviceTypeName] ?? serviceTypeName;
}

export const CANVAS_COLOR_PLUGIN = {
  id: "custom_canvas_background_color",
  /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
  beforeDraw: (chart: any) => {
    const ctx = chart.canvas.getContext("2d");
    ctx.save();
    ctx.globalCompositeOperation = "destination-over";
    ctx.fillStyle = "white";
    ctx.fillRect(0, 0, chart.width, chart.height);
    ctx.restore();
  }
};

export const LABEL_CHART_DRAW = {
  /*eslint-disable-next-line  @typescript-eslint/no-explicit-any*/
  beforeDraw: (chart: any) => {
    const labels = chart.legend.legendItems.map((item: any) => item.text);
    /*eslint-disable-next-line  @typescript-eslint/no-explicit-any*/
    chart.legend.legendItems.forEach((item: any) => {
      const index = labels.indexOf(item.text);
      if (index >= 0) {
        if (item.text === COLLECTIONS_LABELS_TRANSLATE.GROUP_EN) {
          item.text = COLLECTIONS_LABELS_TRANSLATE.GROUP_ES;
        }
        if (item.text === COLLECTIONS_LABELS_TRANSLATE.SECTION_EN) {
          item.text = COLLECTIONS_LABELS_TRANSLATE.SECTION_ES;
        }

        if (
          item.text?.length > 14 &&
          window.matchMedia("(max-width: 768px)").matches
        ) {
          item.text = item.text.substring(0, 14) + "...";
        }
        item.text;
      }
    });
  }
};

export const generalOptions: ChartOptions = {
  scales: {
    yAxes: [
      {
        gridLines: {
          drawBorder: false,
          color: "rgba(29,140,248,0.1)",
          zeroLineColor: "transparent"
        },
        ticks: {
          suggestedMin: 0,
          suggestedMax: 10,
          padding: 20,
          fontColor: "#9a9a9a",
          callback: (value: string) => SERVICE_TYPE_TRANSLATE(value)
        }
      }
    ],
    xAxes: [
      {
        gridLines: {
          drawBorder: false,
          color: "rgba(29,140,248,0.1)",
          zeroLineColor: "transparent"
        },
        ticks: {
          padding: 20,
          fontColor: "#9a9a9a",
          callback: (value: string) => SERVICE_TYPE_TRANSLATE(value)
        }
      }
    ]
  },
  responsive: true,
  maintainAspectRatio: false,
  legend: {
    display: false
  },
  layout: {
    padding: 20
  },
  tooltips: {
    callbacks: {
      /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
      title: function(t: any, d: any) {
        const label = d.labels[t[0].index];
        return SERVICE_TYPE_TRANSLATE(label, d);
      },
      /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
      label: function(t: any, d: any) {
        const value = d.datasets[t.datasetIndex].data[t.index];
        const total = d.datasets[t.datasetIndex].data.reduce(
          (a: number, b: number) => a + b,
          0
        );
        return `Valor: ${value}, ${((value / total) * 100).toFixed(2)}%`;
      }
    }
  },
  animation: {
    duration: 0,
    animateRotate: false,
    animateScale: false
  },
  rotation: 0
};

export const doughnutOptions: ChartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  legend: {
    position: "right"
  },
  layout: {
    padding: 10
  },
  tooltips: {
    callbacks: {
      /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
      title: function(t: any, d: any) {
        const label = d.labels[t[0].index];
        return SERVICE_TYPE_TRANSLATE(label, d);
      },
      /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
      label: function(t: any, d: any) {
        const value = d.datasets[t.datasetIndex].data[t.index];
        const total = d.datasets[t.datasetIndex].data.reduce(
          (a: number, b: number) => a + b,
          0
        );
        return `Valor: ${value}, ${((value / total) * 100).toFixed(2)}%`;
      }
    }
  },
  animation: {
    duration: 0,
    animateRotate: false,
    animateScale: false
  },
  rotation: 0
};

export const TABLE_COLUMNS_GROUP_FILTER = {
  USER: "user",
  ORDER: "order",
  FORM: "form",
  SESSION: "session",
  SERVICE: "service"
};

export const SESSION_HEADERS: TableHeader[] = [
  {
    value: "name",
    text: "Nombre sesión",
    type: "session",
    width: "200px",
    active: true,
    localKey: true
  },
  {
    value: "internalName",
    text: "Nombre servicio",
    type: "session",
    width: "200px",
    active: true,
    localKey: true
  },
  {
    value: "userName",
    text: "Nombre1",
    type: "user",
    active: false,
    localKey: true,
    searched: true,
    width: "170px"
  },
  {
    value: "userMiddleName",
    text: "Nombre2",
    width: "170px",
    type: "user",
    active: false,
    localKey: true,
    searched: true
  },
  {
    value: "userLastName",
    text: "Apellido1",
    type: "user",
    active: false,
    width: "120px",
    localKey: true
  },
  {
    value: "userSecondLastName",
    text: "Apellido2",
    width: "170px",
    type: "user",
    active: false,
    localKey: true,
    searched: true
  },
  {
    value: "userGender",
    text: "Género",
    type: "user",
    active: false,
    localKey: true
  },
  {
    value: "userDocumentType",
    text: "TipoDoc",
    width: "150px",
    type: "user",
    active: false,
    localKey: true
  },
  {
    value: "userDocument",
    text: "Documento",
    width: "170px",
    type: "user",
    active: false,
    localKey: true
  },
  {
    value: "userEmail",
    text: "Correo electrónico",
    width: "170px",
    type: "user",
    active: false,
    localKey: true
  },
  {
    value: "userBirthDay",
    text: "Fecha nacimiento",
    width: "170px",
    type: "user",
    active: false,
    localKey: true
  },
  {
    value: "userContactNumber",
    text: "Número de contacto",
    type: "user",
    active: false,
    localKey: true
  },
  {
    value: "userId",
    text: "ID",
    type: "user",
    active: false,
    localKey: true
  },
  {
    value: "state",
    text: "Estado",
    type: "session",
    active: true,
    localKey: true
  },
  {
    value: "qualification",
    text: "Calificación",
    type: "session",
    active: true,
    localKey: true
  },
  {
    value: "dateCreated",
    text: "Fecha creación",
    type: "session",
    width: "200px",
    active: true,
    localKey: true
  },
  {
    value: "dateChanged",
    text: "Fecha modificación",
    type: "session",
    width: "200px",
    active: false,
    localKey: true
  },
  {
    value: "dateAssigned",
    text: "Fecha de asignación",
    type: "session",
    width: "200px",
    active: false,
    localKey: true
  },
  {
    value: "dateCompleted",
    text: "Fecha de completada",
    type: "session",
    width: "200px",
    active: false,
    localKey: true
  }
];

export const SERVICE_HEADERS: TableHeader[] = [
  {
    value: "serviceInternalName",
    text: "Plantilla de servicio",
    type: "service",
    active: true,
    width: "200px",
    localKey: true
  },
  {
    value: "serviceType",
    text: "Tipo",
    type: "service",
    active: true,
    localKey: true
  },
  {
    value: "serviceProgress",
    text: "Progreso",
    type: "service",
    active: true,
    width: "120px",
    localKey: true
  },
  {
    value: "serviceVisualStateName",
    text: "Estado",
    type: "service",
    active: true,
    localKey: true
  },
  {
    value: "serviceCategory",
    text: "Categoría",
    type: "service",
    active: true,
    width: "120px",
    localKey: true
  },
  {
    value: "cups",
    text: "CUPS",
    type: "service",
    active: true,
    width: "100px",
    localKey: true
  },
  {
    value: "priceLevel",
    text: "Nivel de precio",
    type: "service",
    active: true,
    localKey: true
  },
  {
    value: "userName",
    text: "Nombre1",
    type: "user",
    active: false,
    localKey: true,
    searched: true,
    width: "170px"
  },
  {
    value: "userMiddleName",
    text: "Nombre2",
    width: "170px",
    type: "user",
    active: false,
    localKey: true,
    searched: true
  },
  {
    value: "userLastName",
    text: "Apellido1",
    type: "user",
    active: false,
    width: "120px",
    localKey: true
  },
  {
    value: "userSecondLastName",
    text: "Apellido2",
    width: "170px",
    type: "user",
    active: false,
    localKey: true,
    searched: true
  },
  {
    value: "userGender",
    text: "Género",
    type: "user",
    active: false,
    localKey: true
  },
  {
    value: "userDocumentType",
    text: "TipoDoc",
    width: "150px",
    type: "user",
    active: false,
    localKey: true
  },
  {
    value: "userDocument",
    text: "Documento",
    width: "170px",
    type: "user",
    active: false,
    localKey: true
  },
  {
    value: "userEmail",
    text: "Correo electrónico",
    width: "170px",
    type: "user",
    active: false,
    localKey: true
  },
  {
    value: "userBirthDay",
    text: "Fecha nacimiento",
    width: "170px",
    type: "user",
    active: false,
    localKey: true
  },
  {
    value: "userContactNumber",
    text: "Número de contacto",
    type: "user",
    active: false,
    localKey: true
  },
  {
    value: "userId",
    text: "ID",
    type: "user",
    active: false,
    localKey: true
  },
  {
    value: "billType",
    text: "Tipo de facturación",
    type: "order",
    active: false,
    localKey: false,
    searched: false,
    width: "200px"
  },
  {
    value: "campus",
    text: "Campus",
    type: "order",
    active: false,
    localKey: false,
    searched: false,
    width: "150px"
  },
  {
    value: "nroMedicalOrder",
    text: "Número de orden",
    type: "order",
    active: false,
    localKey: false,
    searched: false,
    width: "200px"
  },
  {
    value: "sectionsFilled",
    text: "Secciones",
    type: "service",
    active: true,
    width: "120px",
    localKey: true,
    sortable: false
  },
  {
    value: "groupsFilled",
    text: "Grupos",
    type: "service",
    active: true,
    width: "120px",
    localKey: true,
    sortable: false
  },
  {
    value: "groupSections",
    text: "Grupos/Secciones",
    type: "service",
    active: true,
    width: "170px",
    localKey: true,
    sortable: false
  }
];
export const CUPS_VALUE = "cups";
export const CUPS_PRICE_LEVEL_VALUE = "priceLevel";
const fixedFields: TableHeader[] = ORDER_CONSTANTS.LIST_FIXED_FIELDS.map(
  item => {
    return {
      value: item.key,
      text: item.name,
      type: TABLE_COLUMNS_GROUP_FILTER.ORDER,
      active: true,
      localKey: true,
      searched: true
    };
  }
);
export const ACTIONS_VALUE = "actions";
export const ORDER_HEADERS: TableHeader[] = [
  {
    value: ACTIONS_VALUE,
    text: "Acciones",
    type: "order",
    active: true,
    localKey: true,
    searched: true
  },
  ...fixedFields,
  {
    value: "serviceCategory",
    text: "Categoría de servicio",
    type: "service",
    active: false,
    width: "120px",
    localKey: true,
    searched: true
  },
  {
    value: "serviceInternalName",
    text: "Plantilla de servicio",
    type: "service",
    active: false,
    width: "200px",
    localKey: true,
    searched: true
  },
  {
    value: "serviceProgress",
    text: "Progreso",
    type: "service",
    active: false,
    width: "120px",
    localKey: true,
    searched: true
  },
  {
    value: "serviceVisualStateName",
    text: "Estado del servicio",
    type: "service",
    active: false,
    width: "120px",
    localKey: true,
    searched: true
  },
  {
    value: "serviceType",
    text: "Tipo de servicio",
    type: "service",
    active: false,
    localKey: true,
    searched: true
  },
  {
    value: "serviceContractType",
    text: "Tipo contrato",
    type: "service",
    active: false,
    width: "120px",
    localKey: true,
    searched: true
  },
  {
    value: "serviceUserType",
    text: "Tipo usuario",
    type: "service",
    active: false,
    width: "120px",
    localKey: true,
    searched: true
  },
  {
    value: "serviceAttentionType",
    text: "Modalidad atención",
    type: "service",
    active: false,
    width: "120px",
    localKey: true,
    searched: true
  },
  {
    value: "serviceChargeConcept",
    text: "Concepto de recaudo",
    type: "service",
    active: false,
    width: "120px",
    localKey: true,
    searched: true
  },
  {
    value: "userContactNumber",
    text: "Número de contacto",
    type: "user",
    active: false,
    localKey: true,
    searched: true
  },
  {
    value: "userDocument",
    text: "Documento",
    type: "user",
    active: false,
    width: "150px",
    localKey: true,
    searched: true
  },
  {
    value: "userDocumentType",
    text: "TipoDoc",
    type: "user",
    active: false,
    width: "150px",
    localKey: true,
    searched: true
  },
  {
    value: "userGender",
    text: "Género",
    type: "user",
    active: false,
    localKey: true,
    searched: true
  },
  {
    value: "userId",
    text: "ID",
    type: "user",
    active: false,
    localKey: true,
    searched: true
  },
  {
    value: "userName",
    text: "Nombre1",
    type: "user",
    active: false,
    width: "120px",
    localKey: true,
    searched: true
  },
  {
    value: "userMiddleName",
    text: "Nombre2",
    width: "170px",
    type: "user",
    active: false,
    localKey: true,
    searched: true
  },
  {
    value: "userLastName",
    text: "Apellido1",
    type: "user",
    active: false,
    width: "120px",
    localKey: true
  },
  {
    value: "userSecondLastName",
    text: "Apellido2",
    width: "170px",
    type: "user",
    active: false,
    localKey: true,
    searched: true
  },
  {
    value: "userEmail",
    text: "Correo electrónico",
    width: "170px",
    type: "user",
    active: false,
    localKey: true
  },
  {
    value: "userBirthDay",
    text: "Fecha nacimiento",
    width: "170px",
    type: "user",
    active: false,
    localKey: true
  },
  {
    value: "serviceAppointmentDate",
    text: "Fecha Cita Servicio",
    type: "service",
    active: false,
    width: "170px",
    localKey: true,
    sortable: true
  }
];

export const FORMS_HEADERS: TableHeader[] = [
  {
    value: "formName",
    text: "Formulario",
    type: "form",
    active: true,
    localKey: true,
    searched: true
  },
  {
    value: "formState",
    text: "Estado",
    type: "form",
    active: true,
    localKey: true,
    searched: true
  },
  {
    value: "formCategory",
    text: "Categoría",
    type: "form",
    active: true,
    localKey: true,
    searched: true
  },
  {
    value: "serviceCategory",
    text: "Categoría",
    type: "service",
    width: "120px",
    active: false,
    localKey: true,
    searched: true
  },
  {
    value: "serviceInternalName",
    text: "Plantilla",
    type: "service",
    width: "200px",
    active: false,
    localKey: true,
    searched: true
  },
  {
    value: "serviceProgress",
    text: "Progreso",
    type: "service",
    width: "120px",
    active: false,
    localKey: true,
    searched: true
  },
  {
    value: "serviceVisualStateName",
    text: "Estado de servicio",
    type: "service",
    active: false,
    localKey: true,
    searched: true
  },
  {
    value: "serviceType",
    text: "Tipo de servicio",
    type: "service",
    active: false,
    localKey: true,
    searched: true
  },
  {
    value: "userName",
    text: "Nombre1",
    type: "user",
    active: false,
    localKey: true,
    searched: true
  },
  {
    value: "userMiddleName",
    text: "Nombre2",
    width: "170px",
    type: "user",
    active: false,
    localKey: true,
    searched: true
  },
  {
    value: "userLastName",
    text: "Apellido1",
    type: "user",
    width: "120px",
    active: false,
    localKey: true,
    searched: true
  },
  {
    value: "userContactNumber",
    text: "Número de contacto",
    type: "user",
    active: false,
    localKey: true,
    searched: true
  },
  {
    value: "userEmail",
    text: "Correo electrónico",
    width: "170px",
    type: "user",
    active: false,
    localKey: true
  },
  {
    value: "userBirthDay",
    text: "Fecha nacimiento",
    width: "170px",
    type: "user",
    active: false,
    localKey: true
  },
  {
    value: "userDocument",
    text: "Documento",
    type: "user",
    width: "150px",
    active: false,
    localKey: true,
    searched: true
  },
  {
    value: "userDocumentType",
    text: "TipoDoc",
    type: "user",
    width: "150px",
    active: false,
    localKey: true,
    searched: true
  },
  {
    value: "userGender",
    text: "Género",
    type: "user",
    width: "120px",
    active: false,
    localKey: true,
    searched: true
  },
  {
    value: "userId",
    text: "ID",
    type: "user",
    active: false,
    localKey: true,
    searched: true
  }
];

export const USERS_HEADERS: TableHeader[] = [
  {
    value: "userDocumentType",
    text: "TipoDoc",
    type: "user",
    active: true,
    localKey: true
  },
  {
    value: "userDocument",
    text: "Documento",
    type: "user",
    active: true,
    localKey: true
  },
  {
    value: "userName",
    text: "Nombre1",
    type: "user",
    active: true,
    localKey: true
  },
  {
    value: "userMiddleName",
    text: "Nombre2",
    type: "user",
    active: true,
    localKey: true
  },
  {
    value: "userLastName",
    text: "Apellido1",
    type: "user",
    active: true,
    localKey: true
  },
  {
    value: "userSecondLastName",
    text: "Apellido2",
    type: "user",
    active: true,
    localKey: true
  },
  {
    value: "userContactNumber",
    text: "Contacto",
    type: "user",
    active: true,
    localKey: true
  },
  {
    value: "userEmail",
    text: "Correo",
    type: "user",
    active: true,
    localKey: true
  },
  {
    value: "userBirthDay",
    text: "Fecha nacimiento",
    width: "170px",
    type: "user",
    active: true,
    localKey: true
  },
  // {
  //   value: "userState",
  //   text: "Estado",
  //   type: "user",
  //   active: true,
  //   localKey: true
  // },
  {
    value: "visualStateName",
    text: "Estado",
    type: "user",
    active: true,
    localKey: true
  },
  {
    value: "userTemplate",
    text: "Plantilla",
    type: "user",
    active: true,
    localKey: true
  },
  {
    value: "userGender",
    text: "Género",
    type: "user",
    active: true,
    localKey: true
  },
  {
    value: "userRoles",
    text: "Roles",
    type: "user",
    active: true,
    localKey: true
  },
  {
    value: "serviceGroups",
    text: "Grupos",
    type: "user",
    active: true,
    localKey: true
  },
  {
    value: "assignedServices",
    text: "Servicios",
    type: "user",
    active: true,
    localKey: true
  },
  {
    value: "serviceSections",
    text: "Secciones",
    type: "user",
    active: true,
    localKey: true
  }
];

export const SERVICE_STATES_ARRAY = [
  { key: SERVICE_STATES.NOT_ASSIGNED, name: SERVICE_STATES.NOT_ASSIGNED },
  { key: SERVICE_STATES.ASSIGNED, name: SERVICE_STATES.ASSIGNED },
  { key: SERVICE_STATES.PROGRESS, name: SERVICE_STATES.PROGRESS },
  { key: SERVICE_STATES.COMPLETED, name: SERVICE_STATES.COMPLETED },
  { key: SERVICE_STATES.CANCELED, name: SERVICE_STATES.CANCELED }
];

export const USER_STATES_ARRAY = [
  { key: USER_STATES.ACTIVE, name: USER_STATES.ACTIVE },
  { key: USER_STATES.INACTIVE, name: USER_STATES.INACTIVE }
];

export const SERVICE_TYPES_ARRAY = [
  { key: SERVICE_TYPES.SECTIONS_KEY, name: SERVICE_TYPES.SECTIONS_NAME },
  { key: SERVICE_TYPES.GROUPS_KEY, name: SERVICE_TYPES.GROUPS_NAME }
];

export const SERVICE_TYPES_STATE_ARRAY = [
  {
    key: SERVICE_TYPES.SERVICE_STATE_KEY,
    name: SERVICE_TYPES.SERVICE_STATE_NAME
  },
  {
    key: SERVICE_TYPES.SERVICE_STATE_NOT_ACTIVE_KEY,
    name: SERVICE_TYPES.SERVICE_STATE_NOT_ACTIVE_NAME
  }
];

export const SESSION_STATES_ARRAY = [
  { key: "Completado", name: "Completado" },
  { key: "En progreso", name: "En progreso" },
  { key: "Cancelado", name: "Cancelado" },
  { key: "asignado", name: "Asignado" }
];

export const QUALIFICATION_STATES_ARRAY = [
  { key: "1", name: "1" },
  { key: "2", name: "2" },
  { key: "3", name: "3" },
  { key: "4", name: "4" },
  { key: "5", name: "5" }
];

export const BILL_TYPE_LIST = [
  { key: "Efectivo", name: "Efectivo" },
  { key: "Tarjeta", name: "Tarjeta" }
];

export const FILTER_DISPLAY = {
  LIST: "list",
  DATE: "picker",
  RANGE: "range",
  SCHEMA: "schema",
  SCHEMA_RANGE: "schema-range",
  INPUT: "input",
  UNIQUE: "unique"
};

export const DATE_OPERATOR = {
  RANGE: "range"
};

export const FILTER_KEYS = {
  COLLECTION: "collection",
  FIELDS: "fields",
  DATE: "filterDate",
  PROGRESS: "progress",
  PROGRESS_OP: "progressOp",
  DATE_START: "dateStart",
  DATE_END: "dateEnd",
  OPTIONS: "options",
  DETAILED_FIELDS: "detailedFields"
};

export const DAY_IN_MS = 86400000;

export const FILTER_TYPES = {
  FORM: "formTemplate",
  SERVICE: "serviceTemplate",
  USER: "userTemplate"
};

export const EQUALS = "igual a";
export const BETWEEN = "entre";

export const TASK_STATES_ARRAY = [
  { key: TASK_STATES.COMPLETED, name: TASK_STATES.COMPLETED },
  { key: TASK_STATES.PENDING, name: TASK_STATES.PENDING },
  { key: TASK_STATES.CANCELED, name: TASK_STATES.CANCELED }
];

export const FILTERS_LIST: Filter[] = [
  {
    mainFilterKey: "service",
    mainFilterName: "Servicios",
    avatar: "mdi-shape-plus",
    placeholder:
      "Escriba el nombre del servicio o utiliza los filtros para agrupar información",
    secondaryFilters: [
      {
        name: "Progreso",
        key: "progress",
        avatar: "mdi-chart-line",
        type: "secondaryFilter",
        display: "range",
        disabled: false
      },
      {
        name: "Estado",
        key: "state",
        avatar: "mdi-state-machine",
        type: "secondaryFilter",
        disabled: false,
        operators: OPERATORS_FILTERS,
        options: SERVICE_STATES_ARRAY,
        display: "list"
      },
      {
        name: "Tipo",
        key: "type",
        avatar: "mdi-account-group",
        type: "secondaryFilter",
        disabled: false,
        operators: OPERATORS_FILTERS,
        options: SERVICE_TYPES_ARRAY,
        display: "list"
      },
      {
        name: "Plantilla",
        key: "serviceTemplate",
        avatar: "mdi-form-select",
        default: true,
        type: "secondaryFilter",
        disabled: false,
        operators: OPERATORS_FILTERS,
        display: "list"
      },
      {
        name: "Nivel",
        key: "serviceLevel",
        avatar: "mdi-form-select",
        default: true,
        type: "secondaryFilter",
        disabled: false,
        operators: OPERATORS_FILTERS,
        display: "list"
      },
      {
        name: "Categoria",
        key: "serviceCategory",
        avatar: "mdi-format-list-bulleted-triangle",
        type: "secondaryFilter",
        disabled: false,
        operators: OPERATORS_FILTERS,
        display: "list"
      },
      {
        name: "Cups",
        key: "servicesCups",
        avatar: "mdi-cash",
        type: "secondaryFilter",
        disabled: false,
        operators: OPERATORS_FILTERS,
        display: "list"
      },
      {
        name: "Fecha de creación",
        key: "dateCreated",
        avatar: "mdi-calendar-month",
        type: "secondaryFilter",
        disabled: true,
        operators: DATE_OPERATORS,
        display: "picker",
        sharedKey: "excluyentFieldsServices"
      },
      {
        name: "Fecha de modificación",
        key: "dateChanged",
        avatar: "mdi-calendar-edit",
        type: "secondaryFilter",
        disabled: false,
        operators: DATE_OPERATORS,
        display: "picker",
        sharedKey: "excluyentFieldsServices"
      }
    ]
  },
  {
    mainFilterKey: "medicalOrder",
    mainFilterName: "Órdenes",
    avatar: "mdi-receipt",
    placeholder:
      "Escriba la identificación del usuario o utiliza los filtros para agrupar información",
    secondaryFilters: [
      {
        name: "Identificación del Usuario",
        key: "user",
        avatar: "mdi-numeric",
        type: "secondaryFilter",
        operators: OPERATORS_FILTERS,
        disabled: false,
        default: true,
        display: "input",
        options: []
      },
      {
        name: "Número de Orden",
        key: "nroMedicalOrder",
        avatar: "mdi-form-textbox",
        type: "secondaryFilter",
        operators: OPERATORS_FILTERS,
        disabled: false,
        default: true,
        display: "input",
        options: []
      },
      {
        name: "Tipo de facturación",
        key: "billType",
        avatar: "mdi-cash-multiple",
        type: "secondaryFilter",
        disabled: false,
        operators: OPERATORS_FILTERS,
        options: BILL_TYPE_LIST,
        display: "list"
      },
      {
        name: "Campos",
        key: "customOrderFields",
        avatar: "mdi-form-textbox",
        type: "secondaryFilter",
        display: "schema",
        sharedKey: "excluyentFieldsOrder"
      },
      {
        name: "Cups",
        key: "orderCups",
        avatar: "mdi-cash",
        disabled: false,
        operators: OPERATORS_FILTERS,
        type: "secondaryFilter",
        display: "list"
      },
      {
        name: "Estado",
        key: "orderState",
        avatar: "mdi-state-machine",
        disabled: false,
        operators: OPERATORS_FILTERS,
        type: "secondaryFilter",
        display: "list"
      },
      {
        name: "Código Diagnostico",
        key: "orderCode",
        avatar: "mdi-symbol",
        disabled: false,
        operators: OPERATORS_FILTERS,
        type: "secondaryFilter",
        display: "list"
      },
      {
        name: "Servicios",
        key: "orderService",
        avatar: "mdi-shape-plus",
        disabled: false,
        operators: OPERATORS_FILTERS,
        type: "secondaryFilter",
        display: "list"
      },
      {
        name: "Fecha de creación",
        key: "dateCreated",
        avatar: "mdi-calendar-month",
        type: "secondaryFilter",
        disabled: false,
        operators: DATE_OPERATORS,
        display: "picker",
        sharedKey: "excluyentFieldsOrder"
      },
      {
        name: "Fecha de modificación",
        key: "dateChanged",
        avatar: "mdi-calendar-edit",
        type: "secondaryFilter",
        disabled: false,
        operators: DATE_OPERATORS,
        display: "picker",
        sharedKey: "excluyentFieldsOrder"
      },
      {
        name: "Fecha de autorización",
        key: "dateAuth",
        avatar: "mdi-calendar-edit",
        type: "secondaryFilter",
        disabled: false,
        operators: DATE_OPERATORS,
        display: "picker",
        sharedKey: "excluyentFieldsOrder"
      },
      {
        name: "Fecha de vencimiento",
        key: "dateExpire",
        avatar: "mdi-calendar-edit",
        type: "secondaryFilter",
        disabled: false,
        operators: DATE_OPERATORS,
        display: "picker",
        sharedKey: "excluyentFieldsOrder"
      }
    ]
  },
  {
    mainFilterKey: "form",
    mainFilterName: "Formularios",
    avatar: "mdi-content-paste",
    placeholder:
      "Escriba el nombre del formulario o utiliza los filtros para agrupar información",
    secondaryFilters: [
      {
        name: "Plantilla de formulario",
        key: "formTemplate",
        avatar: "mdi-form-select",
        type: "secondaryFilter",
        disabled: false,
        operators: OPERATORS_FILTERS,
        display: "list",
        childrenReload: ["formFieldsRange", "formFields"],
        default: true
      },
      {
        name: "Campos de formulario por rango",
        key: "formFieldsRange",
        avatar: "mdi-code-greater-than-or-equal",
        type: "secondaryFilter",
        display: "schema-range",
        sharedKey: "excluyentFieldsForms"
      },
      {
        name: "Campos de formulario",
        key: "formFields",
        avatar: "mdi-form-textbox",
        type: "secondaryFilter",
        display: "schema",
        sharedKey: "excluyentFieldsForms"
      },
      {
        name: "Fecha de creación",
        key: "dateCreated",
        avatar: "mdi-calendar-month",
        type: "secondaryFilter",
        disabled: false,
        operators: DATE_OPERATORS,
        display: "picker",
        sharedKey: "excluyentFieldsForms"
      },
      {
        name: "Fecha de modificación",
        key: "dateChanged",
        avatar: "mdi-calendar-edit",
        type: "secondaryFilter",
        disabled: false,
        operators: DATE_OPERATORS,
        display: "picker",
        sharedKey: "excluyentFieldsForms"
      }
    ]
  },
  {
    mainFilterKey: "user",
    mainFilterName: "Usuarios",
    avatar: "mdi-account",
    placeholder:
      "Escriba la identificación del usuario o utiliza los filtros para agrupar información",
    secondaryFilters: [
      {
        name: "Identificación",
        key: "document",
        avatar: "mdi-numeric",
        type: "secondaryFilter",
        operators: OPERATORS_FILTERS,
        disabled: false,
        default: true,
        display: "input",
        options: []
      },
      {
        name: "Nombre",
        key: "name",
        avatar: "mdi-form-textbox",
        type: "secondaryFilter",
        operators: OPERATORS_FILTERS,
        disabled: false,
        default: true,
        display: "input",
        options: []
      },
      {
        name: "Rol",
        key: "role",
        avatar: "mdi-chart-line",
        type: "secondaryFilter",
        operators: OPERATORS_FILTERS,
        disabled: false,
        notSelect: ["dependency"],
        display: "list"
      },
      {
        name: "Dependencias",
        key: "dependency",
        avatar: "mdi-form-select",
        type: "secondaryFilter",
        operators: OPERATORS_FILTERS,
        disabled: false,
        notSelect: ["role"],
        display: "list"
      },
      {
        name: "Estado",
        key: "state",
        avatar: "mdi-state-machine",
        type: "secondaryFilter",
        operators: OPERATORS_FILTERS,
        options: USER_STATES_ARRAY,
        disabled: false,
        display: "list"
      },
      {
        name: "Plantilla",
        key: "userTemplate",
        avatar: "mdi-form-select",
        type: "secondaryFilter",
        operators: OPERATORS_FILTERS,
        disabled: false,
        childrenReload: ["userFields"],
        display: "list"
      },
      {
        name: "Campos de usuario",
        key: "userFields",
        avatar: "mdi-form-textbox",
        type: "secondaryFilter",
        display: "schema",
        sharedKey: "excluyentFieldsUsers"
      },
      {
        name: "Fecha de creación",
        key: "dateCreated",
        avatar: "mdi-calendar-month",
        type: "secondaryFilter",
        disabled: true,
        operators: DATE_OPERATORS,
        display: "picker",
        sharedKey: "excluyentFieldsUsers"
      },
      {
        name: "Fecha de modificación",
        key: "dateChanged",
        avatar: "mdi-calendar-edit",
        type: "secondaryFilter",
        disabled: false,
        operators: DATE_OPERATORS,
        display: "picker",
        sharedKey: "excluyentFieldsUsers"
      }
    ]
  },
  {
    mainFilterKey: "session",
    mainFilterName: "Sesiones",
    avatar: "mdi-text-box-multiple",
    placeholder:
      "Escriba el nombre de la sesión o utiliza los filtros para agrupar información",
    secondaryFilters: [
      {
        name: "Plantilla",
        key: "sessionTemplates",
        avatar: "mdi-form-select",
        type: "secondaryFilter",
        operators: OPERATORS_FILTERS,
        disabled: false,
        display: "list",
        default: true
      },
      {
        name: "Calificación",
        key: "qualification",
        avatar: "mdi-star",
        type: "secondaryFilter",
        operators: OPERATORS_FILTERS,
        options: QUALIFICATION_STATES_ARRAY,
        disabled: false,
        display: "list"
      },
      {
        name: "Estado",
        key: "state",
        avatar: "mdi-state-machine",
        type: "secondaryFilter",
        operators: OPERATORS_FILTERS,
        options: SESSION_STATES_ARRAY,
        disabled: false,
        display: "list"
      },
      {
        name: "Fecha de creación",
        key: "dateCreated",
        avatar: "mdi-calendar-month",
        type: "secondaryFilter",
        disabled: false,
        operators: DATE_OPERATORS,
        display: "picker",
        sharedKey: "sessionsDateKey"
      },
      {
        name: "Fecha de modificación",
        key: "dateChanged",
        avatar: "mdi-calendar-edit",
        type: "secondaryFilter",
        disabled: false,
        operators: DATE_OPERATORS,
        display: "picker",
        sharedKey: "sessionsDateKey"
      }
    ]
  },
  {
    mainFilterKey: "task",
    mainFilterName: "Tareas",
    avatar: "mdi-format-list-checks",
    placeholder:
      "Escriba el nombre de la tarea o utiliza los filtros para agrupar información",
    secondaryFilters: [
      {
        name: "Plantilla de formulario",
        key: "formTemplate",
        type: "secondaryFilter",
        disabled: false,
        operators: OPERATORS_FILTERS,
        display: "list",
        childrenReload: ["formFieldsRange", "formFields"],
        default: true
      },
      {
        name: "Estado",
        key: "state",
        type: "secondaryFilter",
        disabled: false,
        operators: OPERATORS_FILTERS,
        display: "list",
        options: TASK_STATES_ARRAY
      },
      //actualmente no se esta utilizando filtro por dependencias
      // {
      //   name: "Dependencia",
      //   key: "dependency",
      //   type: "secondaryFilter",
      //   disabled: false,
      //   operators: OPERATORS_FILTERS,
      //   display: "list"
      // },
      {
        name: "Documento del usuario asignado",
        key: "responsible",
        avatar: "mdi-calendar-edit",
        type: "secondaryFilter",
        disabled: false,
        operators: OPERATORS_FILTERS,
        display: "input"
      },
      {
        name: "Fecha de creación",
        key: "dateCreated",
        type: "secondaryFilter",
        disabled: false,
        operators: DATE_OPERATORS,
        display: "picker",
        sharedKey: "tasksDateKey"
      },
      {
        name: "Fecha de modificación",
        key: "dateChanged",
        avatar: "mdi-calendar-edit",
        type: "secondaryFilter",
        disabled: false,
        operators: DATE_OPERATORS,
        display: "picker",
        sharedKey: "tasksDateKey"
      },
      {
        name: "Usuario creación",
        key: "createdBy",
        avatar: "mdi-calendar-edit",
        type: "secondaryFilter",
        disabled: false,
        operators: OPERATORS_FILTERS,
        display: "input"
      }
    ]
  }
];

export const DASHBOARD_MESSAGES = {
  NO_RESULTS: "Sin resultados",
  NOT_FOUND: "No se encontró información con el filtro de búsqueda",
  FIELD_NOT_FOUND: "No se encontró información para los campos",
  NO_FORM_TEMPLATE: "Seleccione la plantilla primero",
  NOT_ALLOWED:
    "No es posible realizar búsquedas por campos personalizados y fechas",
  PARTIAL_SUCCESS: "Operación realizada parcialmente",
  GENERIC_SUCCESS: "Operación realizada correctamente",
  NO_RANGE_FIELDS:
    "La plantilla seleccionada no tiene campos filtrables por rango",
  NO_VALUE_FIELDS:
    "La plantilla seleccionada no tiene campos filtrables por valor",
  MAX_MULTIPLE_FILTER_REACH: "Solo puede escoger un maximo de 10 elementos"
};

export const CUSTOM_FIELD_KEYS = ["customOrderFields", "formFields"];
export const DATE_KEYS = ["dateCreated", "dateChanged"];

export const DASHBOARD_LABELS = {
  CUSTOM_FIELDS: "Campos personalizados",
  RANGE_FIELDS: "Campos por rango",
  LOAD_MORE: "Cargar otras plantillas...",
  LOADING: "Consultando información",
  GROUP_BY: "Agrupar por:",
  NO_SECTIONS: "No hay secciones iniciadas",
  NO_SERVICES: "No hay servicios iniciados",
  NO_ROLE: "No hay roles asociados",
  NO_GROUPS: "No hay grupos iniciados",
  INVALID: "No aplica",
  RESULTS: "Resultados para",
  START: "Seleccione los filtros",
  COLUMNS_MENU: "Añadir/Eliminar columnas",
  ACTIONS_MENU: "Mostrar/Ocultar acciones",
  EXPORT_EXCEL: "Generar Excel",
  FIELDS_LABEL: "Campos",
  BACK_LEVEL_FILTER: "Regresar al agrupador",
  GROUP_BY_DEEPLEVEL: "Subagrupar por: ",
  UPLOAD_MULTIPLE_ORDERS: "Carga masiva de ordenes",
  SELECT_STATE: "Seleccione el estado"
};

export const CHART_TYPES = [
  "Barras",
  "Barras horizontales",
  "Torta",
  "Dona",
  "Línea"
];

export const OFFSET_DATE = 7;
export const LAST_X_DAYS = new Date(Date.now() - DAY_IN_MS * OFFSET_DATE);

export const INITIAL_FILTER = {
  collection: "service",
  medicalOrder: {},
  service: {},
  filterDate: "dateCreated",
  dateStart: Date.now() - DAY_IN_MS * OFFSET_DATE
};

export const INITIAL_FILTER_CHIP = {
  filterKey: "dateCreated",
  name: "Fecha de creación",
  operator: ">=",
  value: LAST_X_DAYS.toISOString().substr(0, 10),
  displayValue: LAST_X_DAYS.toISOString().substr(0, 10),
  parentFilter: "service",
  type: "picker"
};

export const EMPTY_STATE_HEADER_EDIT_MEDICAL_ORDER = "Sin campos para editar";
export const LACK_OF_OPTIONS = "La lista de opciones no tiene mas elementos";
export const EMPTY_STATE_PARAGRAPH_EDIT_MEDICAL_ORDER =
  "La plantilla de órdenes no tiene campos que puedan ser editados desde el dashboard.";

export const DOWNLOAD_MODAL_TITLE = "Descargar";
export const DOWNLOAD_MODAL_TEXT =
  "¿Está seguro que desea descargar la información presente del tablero?";
export const DOWNLOAD_MODAL_TEXT_FULL =
  "¿Esta seguro que desea descargar toda la informacion del tablero?";

export const DOWNLOAD_FILE_GENERATED_TITLE = "Archivo generado";
export const DOWNLOAD_FILE_GENERATED_TEXT =
  "Se ha iniciado el proceso para exportar la informacion, le avisaremos en su linea de tiempo cuando este listo";

export const DOWNLOAD_AND_UPDATE_TITLE = "Descargar y Actualizar";
export const DOWNLOAD_AND_UPDATE_TEXT =
  "¿Está seguro que desea descargar y actualizar el estado de las ordenes del tablero?";
export const DOWNLOAD_AND_UPDATE_BTN = "Descargar y actualizar";
export const DOWNLOAD_BTN = "Solo Descargar";
export const CANCEL_BTN = "Cancelar";

export const FILTER_CONSTANTS = {
  QUALIFICATION: "qualification",
  GROUPSECTION_SESSION: "groupSections.$.session",
  GROUPSECTION_SESSIONQUALIFICATION: "groupSections.$.session.qualification",
  SESSION_QUALIFICATION_DEEPLABEL: "session.deepLevel.qualification.labels.$",
  SESSION_QUALIFICATION_DEEPVALUE: "session.deepLevel.qualification.values.$"
};

export const UNRATED_OR_NOAPPLY = "No aplica";

export const NO_GROUP_LABEL = "Sin Grupo";

export const ID_LIST = {
  SERVICE_CATEGORY: "BPQCt1csjU5JBHjVP6pt",
  ORDER_STATE: "LE5NOLPoQNab9xbmecva",
  ORDER_CODE: "GNVJAkLSOPNkXtWqz7vA"
};

export const DEFAULT_SEARCH_OBJECT = {
  state: "Activo"
};

export const EXECUTABLE_LIST = {
  SERVICE_CATEGORY: "serviceCategory",
  ORDER_STATE: "orderState",
  ORDER_CODE: "orderCode",
  SERVICES_CUPS: "servicesCups",
  ORDER_CUPS: "orderCups",
  ORDER_SERVICE: "orderService"
};

export const MAIN_FILTER: { [key: string]: string } = {
  forms: "Formularios",
  services: "Servicios",
  medicalOrders: "Órdenes",
  sessions: "Sesiones",
  users: "Usuarios"
};

export const FILTER_STATE_ACTIVATION: {
  [key: string]: boolean;
} = {
  Formularios: false,
  Servicios: false,
  Usuarios: false,
  Sesiones: false
};

export const TAG_FILTER_NAMES = [
  "Formularios",
  "Servicios",
  "Usuarios",
  "Órdenes",
  "Sesiones"
];

export const FILTERED_DASHBOARD_TAGS = {
  SERVICES: "services",
  SEARCH: "search",
  FORMS: "forms"
};
export const SECONDARYFILTERS_NAMES = {
  USER_FIELDS: "Campos de usuario",
  CUSTOM_ORDER_FIELDS: "customOrderFields",
  FORM_FIELDS: "formFields",
  SECONDARY_FILTER: "secondaryFilter",
  OPERATORS: "operators",
  MEDICAL_ORDER: "medicalOrder",
  FORM_FIELDS_RANGE: "formFieldsRange",
  ORDERTEMPLATESCHEMA: "orderTemplateSchema",
  FORMTEMPLATESCHEMA: "formTemplateSchema",
  USERSCHEMA: "userSchema"
};
export const DISPLAYCOMPONENT_ACTIONS = {
  SHOWSCHEMARANGE: "showSchemaRange",
  SHOWTREEVIEW: "showTreeView",
  SHOWLIST: "showList",
  SHOWRANGE: "showRange",
  SHOWDATEPICKER: "showDatePicker",
  SHOWINPUT: "showInput"
};

export const OBJECT_TYPE = "object";

export const CLEAR_FILTER_TOOLTIP = "Limpiar Filtros";
export const ADD_TOOLTIP = "Agregar";
export const SEARCH_TOOLTIP = "Buscar";
export const DEFAULT_PLACEHOLDER = "Usa los filtros para agrupar información";
export const GETINFO = "getInfo";
export const SEARCH = "search";
export const DISABLE = "disable";
export const ENABLE = "enable";
export const CHANGE_MULTIPLE_OPTIONS = {
  ADD: "sumar",
  SUB: "restar"
};
export const INITIAL_PLACEHOLDER =
  "Tiene filtros seleccionados para realizar una busqueda";
export const ATTRIBUTES_OF_DASHBOARD_FILTER = [
  "bigSearch",
  "collection",
  "dateEnd",
  "dateStart",
  "filterDate"
];
export const ATTRIBUTES_OF_DASHBOARD_FILTER_OBJECT = {
  dateEnd: "dateEnd",
  dateStart: "dateStart",
  filterDate: "filterDate"
};
export const PATIENT = "Paciente";

export const ACTIVE = "Activo";
export const INACTIVE = "Inactivo";

export const DEFAULT_GROUP_BY_TYPE_INDEX = 0;

export const GROUP_BY_FIELDS_SERVICE = [
  {
    name: "Nombre interno",
    convert: "serviceTemplate",
    value: "serviceInternalName"
  },
  { name: "Tipo", convert: "serviceType", value: "serviceType" },
  { name: "Estado", convert: "state", value: "serviceState" },
  { name: "Categoría", convert: "serviceCategory", value: "serviceCategory" },
  { name: "Cups", convert: "cups", value: "cups" }
];

export const GROUP_BY_FIELDS_MEDICAL_ORDERS = [
  { name: "Estado", value: "state", convert: "orderState" },
  { name: "Cups", value: "cups", convert: "cups" },
  {
    name: "Código Diagnóstico",
    value: "diagnosticPrimaryCode",
    convert: "orderCode"
  },
  { name: "Tipo de facturación", value: "billType", convert: "billType" },
  { name: "Campus", value: "campus", convert: "campus" },
  { name: "Servicio", value: "serviceInternalName", convert: "orderService" }
];

export const GROUP_BY_FIELDS_FORMS = [
  { name: "Nombre", value: "formName", convert: "formTemplate" },
  { name: "Estado", value: "formState", convert: "state" },
  { name: "Categoria", value: "formCategory", convert: "category" }
];

export const GROUP_BY_FIELDS_USERS = [
  {
    name: "Dependencias",
    value: "userDependencies",
    convert: "userDependencies"
  },
  { name: "Roles", value: "userRoles", convert: "userRoles" },
  { name: "Estado", value: "userState", convert: "state" }
];

export const GROUP_BY_FIELDS_TASK = [
  { name: "Formulario", value: "taskFormTemplate", convert: "formTemplate" },
  { name: "Estado", value: "taskState", convert: "state" }
];

export const GROUP_BY_FIELDS_SESSIONS = [
  { name: "Nombre", value: "name", convert: "sessionTemplates" },
  { name: "Estado", value: "state", convert: "state" },
  { name: "Nombre interno", value: "internalName", convert: "internalName" },
  { name: "Calificación", value: "qualification", convert: "qualification" }
];

export const GROUP_BY_FIELDS_BY_COLLECTION = {
  service: GROUP_BY_FIELDS_SERVICE,
  form: GROUP_BY_FIELDS_FORMS,
  medicalOrder: GROUP_BY_FIELDS_MEDICAL_ORDERS,
  session: GROUP_BY_FIELDS_SESSIONS,
  task: GROUP_BY_FIELDS_TASK,
  user: GROUP_BY_FIELDS_USERS
};

export const TEXT_LABELS = {
  LIST_FILTER: "Filtros",
  CLEAR_FILTER: "Limpiar filtros",
  APLLY_FILTER: "Aplicar filtro",
  CLOSE_FILTER: "Cerrar filtros",
  CLOSE_SEARCH: "Cerrar busqueda"
};

export const USERS = "users";

export const MAIN_FILTER_FOR_INNER_GROUPING = ["medicalOrders", "sessions"];

export const INNER_GROUPERS = [
  "serviceInternalName",
  "userGender",
  "userDocumentType"
];

export const DASBOARD_CURRENT_FILTER = {
  FORMS: "forms",
  SESSIONS: "sessions",
  SERVICES: "services",
  ORDERS: "medicalOrders"
};

export const COLUMN_FILTER_UPDATE = {
  [DASBOARD_CURRENT_FILTER.SERVICES]: [
    { group: TABLE_COLUMNS_GROUP_FILTER.SERVICE, allActive: true },
    { group: TABLE_COLUMNS_GROUP_FILTER.ORDER, allActive: false },
    { group: TABLE_COLUMNS_GROUP_FILTER.USER, allActive: false }
  ],
  [DASBOARD_CURRENT_FILTER.FORMS]: [
    { group: TABLE_COLUMNS_GROUP_FILTER.FORM, allActive: true },
    { group: TABLE_COLUMNS_GROUP_FILTER.SERVICE, allActive: false },
    { group: TABLE_COLUMNS_GROUP_FILTER.USER, allActive: false }
  ],
  [DASBOARD_CURRENT_FILTER.SESSIONS]: [
    { group: TABLE_COLUMNS_GROUP_FILTER.SESSION, allActive: true },
    { group: TABLE_COLUMNS_GROUP_FILTER.SERVICE, allActive: false },
    { group: TABLE_COLUMNS_GROUP_FILTER.USER, allActive: false }
  ],
  [DASBOARD_CURRENT_FILTER.ORDERS]: [
    { group: TABLE_COLUMNS_GROUP_FILTER.SERVICE, allActive: false },
    { group: TABLE_COLUMNS_GROUP_FILTER.USER, allActive: false }
  ]
};

export const TASKS_HEADERS: TableHeader[] = [
  {
    value: "_key",
    text: "ID",
    type: "task",
    width: "200px",
    active: false,
    localKey: true
  },
  {
    value: "taskName",
    text: "Nombre tarea",
    type: "task",
    width: "200px",
    active: true,
    localKey: true
  },
  {
    value: "taskFormTemplate",
    text: "Formulario",
    type: "task",
    width: "200px",
    active: true,
    localKey: true
  },
  {
    value: "taskState",
    text: "Estado",
    type: "task",
    active: true,
    localKey: true,
    width: "170px"
  },
  {
    value: "taskDateCreated",
    text: "Fecha de creación",
    type: "task",
    active: true,
    localKey: true,
    width: "190px"
  },
  {
    value: "taskDateCompleted",
    text: "Fecha de completado",
    type: "task",
    active: true,
    localKey: true,
    width: "190px"
  },
  {
    value: "taskDateCanceled",
    text: "Fecha de cancelación",
    type: "task",
    active: true,
    localKey: true,
    width: "190px"
  },

  {
    value: "taskDateElapsed",
    text: "Tiempo transcurrido (días)",
    type: "task",
    active: true,
    localKey: true,
    width: "190px",
    sortable: false
  },
  // {
  //   value: "responsableId",
  //   text: "Documento del Responsable",
  //   type: "task",
  //   active: true,
  //   localKey: true,
  //   width: "190px",
  //   sortable: false
  // },
  // {
  //   value: "responsableName",
  //   text: "Nombre del Responsable",
  //   type: "task",
  //   active: false,
  //   localKey: true,
  //   width: "190px",
  //   sortable: false
  // },
  {
    value: "responsable",
    text: "Responsable",
    type: "task",
    active: false,
    localKey: true,
    width: "190px",
    sortable: false
  },
  // actualmente no se esta utilizando filtro de tareas por dependencia
  {
    value: "taskDependencies",
    text: "Dependencias",
    type: "task",
    active: true,
    localKey: true,
    width: "170px"
  },
  {
    value: "userName",
    text: "Nombre1",
    type: "user",
    active: false,
    localKey: true,
    searched: true
  },
  {
    value: "userMiddleName",
    text: "Nombre2",
    width: "170px",
    type: "user",
    active: false,
    localKey: true,
    searched: true
  },
  {
    value: "userLastName",
    text: "Apellido1",
    type: "user",
    width: "120px",
    active: false,
    localKey: true,
    searched: true
  },
  {
    value: "userContactNumber",
    text: "Número de contacto",
    type: "user",
    active: false,
    localKey: true,
    searched: true
  },
  {
    value: "userEmail",
    text: "Correo electrónico",
    width: "170px",
    type: "user",
    active: false,
    localKey: true
  },
  {
    value: "userBirthDay",
    text: "Fecha nacimiento",
    width: "170px",
    type: "user",
    active: false,
    localKey: true
  },
  {
    value: "userDocument",
    text: "Documento",
    type: "user",
    width: "150px",
    active: false,
    localKey: true,
    searched: true
  },
  {
    value: "userDocumentType",
    text: "TipoDoc",
    type: "user",
    width: "150px",
    active: false,
    localKey: true,
    searched: true
  },
  {
    value: "userGender",
    text: "Género",
    type: "user",
    width: "120px",
    active: false,
    localKey: true,
    searched: true
  },
  {
    value: "userId",
    text: "ID",
    type: "user",
    active: false,
    localKey: true,
    searched: true
  }
];

export const DATE_FORMAT = "YYYY-MM-DD, h:mm a";

export const ORDER_GEN_UTILS = {
  SEPATORS: [": ", "-"],
  RULES_ROUTE: "/general-rules"
};

export const ORDER_UPDATE_STATES = [
  { key: ORDER_STATES.AUTHORIZED, name: ORDER_STATES.AUTHORIZED },
  { key: ORDER_STATES.CANCELED, name: ORDER_STATES.CANCELED },
  { key: ORDER_STATES.COMPLETED, name: ORDER_STATES.COMPLETED },
  { key: ORDER_STATES.NEW, name: ORDER_STATES.NEW }
];

export const UPDATE_ORDER_CONTANTS = {
  SWITCH_TITLE: "Actualizar Estado",
  SELECT_TITLE: "Estado a Actualizar",
  KEY: "key",
  NAME: "name"
};

export const INIT_PAGE = 1;
export const SERVICES = "services";

export const CHANGE_STATE_EVENT = "changeState";
export const CANCEL_CHANGE_SERVICE_STATE = "cancelChangeServiceState";
